import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const isStaging = import.meta.env.VITE_FIREBASE_PROJECT_ID === 'socialsnap-testing';

const TEST_ACCOUNTS = [
  { email: 'superadmin@test.socialsnap.pics', label: 'Super Admin', color: 'red' },
  { email: 'admin@test.socialsnap.pics', label: 'Admin', color: 'orange' },
  { email: 'orgowner@test.socialsnap.pics', label: 'Alpha Owner', color: 'blue' },
  { email: 'orgowner2@test.socialsnap.pics', label: 'Beta Owner', color: 'blue' },
  { email: 'manager@test.socialsnap.pics', label: 'Org Manager', color: 'purple' },
  { email: 'sales@test.socialsnap.pics', label: 'Salesperson', color: 'green' },
];

const TEST_PASSWORD = 'Testing123!';

// Dev-only role override (original behavior)
const DEV_ROLES = [
  { key: null, label: 'Actual Role', color: 'gray' },
  { key: 'superadmin', label: 'Super Admin', color: 'red' },
  { key: 'org_owner', label: 'Org Owner', color: 'blue' },
  { key: 'salesperson', label: 'Salesperson', color: 'green' },
  { key: 'affiliate', label: 'Affiliate', color: 'purple' },
  { key: 'social_media', label: 'Marketing Mgr', color: 'orange' },
];

const colorClasses = {
  gray: 'bg-gray-700 hover:bg-gray-600',
  red: 'bg-red-600 hover:bg-red-500',
  orange: 'bg-orange-600 hover:bg-orange-500',
  blue: 'bg-blue-600 hover:bg-blue-500',
  purple: 'bg-purple-600 hover:bg-purple-500',
  green: 'bg-green-600 hover:bg-green-500',
};

const DevRoleSwitcher = () => {
  const { currentUser, signIn, devRoleOverride, setDevRoleOverride, actualRole } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Show account switcher on staging, role override in dev
  if (!isStaging && !import.meta.env.DEV) return null;

  // ── Staging: real account switcher ──────────────────────────
  if (isStaging) {
    const currentAccount = TEST_ACCOUNTS.find(a => a.email === currentUser?.email);
    const buttonColor = currentAccount ? colorClasses[currentAccount.color] : colorClasses.gray;
    const buttonLabel = currentAccount ? currentAccount.label : 'Switch Account';

    const handleSwitch = async (account) => {
      if (account.email === currentUser?.email) {
        setExpanded(false);
        return;
      }
      setSwitching(true);
      try {
        await signIn(account.email, TEST_PASSWORD);
        setExpanded(false);
      } catch (err) {
        console.error('Account switch failed:', err.message);
        alert(`Switch failed: ${err.message}\n\nDid you run the seed script?`);
      } finally {
        setSwitching(false);
      }
    };

    return (
      <div className="fixed bottom-4 right-4 z-[100]">
        {expanded && (
          <div className="mb-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden w-56">
            <div className="px-3 py-2 bg-amber-50 border-b border-amber-200">
              <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wider">Staging Account Switcher</p>
              <p className="text-xs text-gray-500">All use password: Testing123!</p>
            </div>
            {TEST_ACCOUNTS.map((account) => {
              const isCurrent = currentUser?.email === account.email;
              return (
                <button
                  key={account.email}
                  onClick={() => handleSwitch(account)}
                  disabled={switching}
                  className={`w-full text-left px-3 py-2 text-sm transition ${
                    isCurrent
                      ? 'bg-amber-50 text-amber-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  } ${switching ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${colorClasses[account.color].split(' ')[0]}`} />
                      {account.label}
                    </div>
                    {isCurrent && <span className="text-xs text-amber-500">current</span>}
                  </div>
                  <p className="text-[10px] text-gray-400 ml-4">{account.email}</p>
                </button>
              );
            })}
          </div>
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          className={`${buttonColor} text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg transition flex items-center gap-1.5`}
        >
          {switching ? (
            <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <span className="w-2 h-2 rounded-full bg-white/40" />
          )}
          {switching ? 'Switching...' : buttonLabel}
        </button>
      </div>
    );
  }

  // ── Dev mode: role override (original behavior) ─────────────
  if (!setDevRoleOverride) return null;

  const activeRole = DEV_ROLES.find(r => r.key === devRoleOverride) || DEV_ROLES[0];

  return (
    <div className="fixed bottom-4 right-4 z-[100]">
      {expanded && (
        <div className="mb-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden w-48">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Dev Role Switcher</p>
            <p className="text-xs text-gray-500">Actual: <span className="font-medium">{actualRole}</span></p>
          </div>
          {DEV_ROLES.map((role) => (
            <button
              key={role.key || 'actual'}
              onClick={() => {
                setDevRoleOverride(role.key);
                setExpanded(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm transition ${
                devRoleOverride === role.key
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {role.label}
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`${colorClasses[activeRole.color]} text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg transition flex items-center gap-1.5`}
      >
        <span className="w-2 h-2 rounded-full bg-white/40"></span>
        {devRoleOverride ? activeRole.label : 'DEV'}
      </button>
    </div>
  );
};

export default DevRoleSwitcher;
